import { Routes } from '@angular/router';
import { CommonRoute } from '../common/enums/common-route.enum';
import { SupportEnabledGuard } from './features/support/guards/support-enabled.guard';
import { AccessControlGuard } from '../common/guards/access-control.guard';
import { LoginGuard } from '../common/guards/login.guard';
import { CountryAvailabilityGuard } from '../common/guards/country-availability.guard';
import { SignUpChooserGuard } from './core/access-control/authentication/register-choice/sign-up-chooser.guard';
import { AccessDeniedComponent } from './core/access-control/authorization/access-denied/access-denied.component';
import { NotFoundComponent } from './core/pages/not-found/not-found.component';
import { purchaseEnabledGuard } from './core/payment/guards/purchase-enabled.guard';

// Auth + chrome routes (standalone login / logout / error screens). The Sequal
// browser-app tabs are the app surface and are mounted at '/' (see sequalRoutes).
// The auth screens load lazily: a returning, signed-in user never opens them, so
// they stay out of the main bundle. The error pages stay eager so they still
// render when a chunk cannot be fetched.
const authRoutes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./core/access-control/authentication/login/login.component').then((m) => m.LoginComponent),
    canActivate: [LoginGuard],
  },
  {
    path: 'logout',
    loadComponent: () =>
      import('./core/access-control/authentication/logout/logout.component').then((m) => m.LogoutComponent),
  },
  { path: 'not-found', component: NotFoundComponent },
  { path: 'access-denied', pathMatch: 'full', component: AccessDeniedComponent },
  {
    path: 'country-unavailable',
    loadComponent: () =>
      import('./core/pages/country-unavailable/country-unavailable.component').then(
        (m) => m.CountryUnavailableComponent,
      ),
  },
  {
    path: 'sign-up',
    loadComponent: () =>
      import('./core/access-control/authentication/register-choice/register-choice.component').then((m) => m.RegisterChoiceComponent),
    // Country first, so a user outside the release is sent to say so rather
    // than on to a form. With organisations off, the chooser is skipped before
    // it ever loads (ADR-0026).
    canActivate: [CountryAvailabilityGuard, SignUpChooserGuard],
  },
  {
    path: 'register-individual',
    loadComponent: () =>
      import('./core/access-control/authentication/register/register.component').then((m) => m.RegisterComponent),
    canActivate: [CountryAvailabilityGuard],
  },
  {
    path: 'register-organization',
    loadComponent: () =>
      import('./core/access-control/authentication/register-organization/register-organization.component').then((m) => m.RegisterOrganizationComponent),
    canActivate: [CountryAvailabilityGuard],
  },
  {
    path: 'mfa-setup',
    data: { mode: 'forced-setup' },
    loadComponent: () =>
      import(
        './core/access-control/authentication/mfa/mfa-setup/mfa-setup.component'
      ).then((m) => m.MfaSetupComponent),
  },
  {
    path: 'verify-email/:token',
    loadComponent: () =>
      import('./core/access-control/authentication/verify-email/verify-email.component').then((m) => m.VerifyEmailComponent),
  },
  {
    // Reachable signed out: the confirm link is opened in the NEW inbox, often
    // on a different device from the one that requested the change.
    path: 'verify-email-change/:token',
    loadComponent: () =>
      import(
        './core/access-control/authentication/verify-email-change/verify-email-change.component'
      ).then((m) => m.VerifyEmailChangeComponent),
  },
  {
    path: 'recover-password',
    loadComponent: () =>
      import('./core/access-control/authentication/recover-password/request-password-reset.component').then((m) => m.RequestPasswordResetComponent),
  },
  {
    path: 'reset-password/:token',
    pathMatch: 'full',
    loadComponent: () =>
      import('./core/access-control/authentication/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
  },
  {
    path: 'apple-signin',
    loadComponent: () =>
      import('./core/access-control/authentication/external-provider/apple/apple-signin/apple-signin.component').then((m) => m.AppleSigninComponent),
  },
  {
    path: 'reset-mfa/:token',
    pathMatch: 'full',
    loadComponent: () =>
      import('./core/access-control/authentication/mfa/reset-mfa/reset-mfa.component').then((m) => m.ResetMfaComponent),
  },
  // Polar Checkout return URL. Stays at root so success_url / cancel_url
  // stay stable across the Sequal route shuffle.
  {
    path: 'payment/callback',
    loadComponent: () =>
      import('./core/payment/payment-callback.component').then(
        (m) => m.PaymentCallbackComponent,
      ),
  },
  {
    path: 'payment/checkout-confirmation',
    canActivate: [AccessControlGuard, CountryAvailabilityGuard],
    loadComponent: () =>
      import('./core/payment/checkout-confirmation/checkout-confirmation.component').then(
        (m) => m.CheckoutConfirmationComponent,
      ),
  },
  // Branded checkout magic-link landing (ADR-0012). The emailed link points
  // here; it obtains country-specific terms and redirects to hosted checkout
  // only after explicit consent. Root-level so the link stays stable;
  // token-guarded + GUEST-accessible (no session).
  {
    path: 'checkout/:token',
    canActivate: [CountryAvailabilityGuard, purchaseEnabledGuard],
    loadComponent: () =>
      import('./core/payment/checkout-redeem.component').then(
        (m) => m.CheckoutRedeemComponent,
      ),
  },
  // Web-only checkout cart (authenticated). The redeem landing above is
  // GUEST-accessible (token-guarded); the cart itself requires sign-in.
  {
    path: 'cart',
    canActivate: [
      AccessControlGuard,
      CountryAvailabilityGuard,
      purchaseEnabledGuard,
    ],
    loadComponent: () =>
      import('./core/payment/cart/cart.component').then((m) => m.CartComponent),
  },
  {
    path: 'request-mfa-reset',
    canActivate: [AccessControlGuard],
    loadComponent: () =>
      import(
        './core/access-control/authentication/mfa/request-mfa-reset/request-mfa-reset.component'
      ).then((m) => m.RequestMfaResetComponent),
  },
];

// Account-management deep links. No tab anchor -- entered from the Sequal
// Settings page ("Account" section) when the user is signed in. Auth-guarded.
const accountRoutes: Routes = [
  {
    path: 'account',
    canActivateChild: [AccessControlGuard],
    children: [
      {
        path: 'profile',
        loadComponent: () =>
          import('./core/pages/settings/profile/profile.component').then(
            (m) => m.ProfileComponent,
          ),
      },
      {
        path: 'change-password',
        loadComponent: () =>
          import(
            './core/pages/settings/change-password/change-password.component'
          ).then((m) => m.ChangePasswordComponent),
      },
      {
        path: 'change-email',
        loadComponent: () =>
          import(
            './core/pages/settings/change-email/change-email.component'
          ).then((m) => m.ChangeEmailComponent),
      },
      {
        // Security settings list, then MFA one level down. Both URLs already
        // carry FRONTEND_URL grants for every app role.
        path: 'security-mfa',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './core/pages/settings/security/security.component'
              ).then((m) => m.SecurityComponent),
          },
          {
            path: 'setup',
            loadComponent: () =>
              import(
                './core/access-control/authentication/mfa/mfa-setup/mfa-setup.component'
              ).then((m) => m.MfaSetupComponent),
          },
        ],
      },
      {
        path: 'phone-management',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './core/pages/settings/phone-management/phone-management.component'
              ).then((m) => m.PhoneManagementComponent),
          },
          {
            path: 'mobile-verification',
            loadComponent: () =>
              import(
                './core/pages/settings/phone-management/mobile-verification/mobile-verification.component'
              ).then((m) => m.MobileVerificationComponent),
            data: { isAlternative: false },
          },
          {
            path: 'alternative-mobile-verification',
            loadComponent: () =>
              import(
                './core/pages/settings/phone-management/mobile-verification/mobile-verification.component'
              ).then((m) => m.MobileVerificationComponent),
            data: { isAlternative: true },
          },
        ],
      },
      {
        path: 'organization-users',
        loadComponent: () =>
          import(
            './core/pages/settings/organization-users/organization-users.component'
          ).then((m) => m.OrganizationUsersComponent),
      },
      {
        path: 'organization-audit',
        loadComponent: () =>
          import(
            './core/pages/settings/organization-audit/organization-audit.component'
          ).then((m) => m.OrganizationAuditComponent),
      },
      {
        path: 'organization-branding',
        loadComponent: () =>
          import(
            './features/sequal/pages/organization-branding/organization-branding.component'
          ).then((m) => m.OrganizationBrandingComponent),
      },
      {
        path: 'orders',
        loadComponent: () =>
          import('./core/pages/settings/orders/orders.component').then(
            (m) => m.OrdersComponent,
          ),
      },
      {
        path: 'sessions',
        loadComponent: () =>
          import(
            './core/pages/settings/device-sessions/device-sessions.component'
          ).then((m) => m.DeviceSessionsComponent),
      },
    ],
  },
];

// Messaging. Premium-gated at use-site (triggered from a user's listing,
// e.g. "Message creator" on a published sequence). Not rendered as a tab
// and not on a launcher. Auth-guarded here; the server endpoints sit behind
// PremiumGuard so non-premium callers get a 403.
const messageRoutes: Routes = [
  {
    path: 'message',
    canActivate: [CountryAvailabilityGuard],
    canActivateChild: [AccessControlGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './features/messaging/pages/conversation-list/conversation-list.component'
          ).then((m) => m.ConversationListComponent),
      },
      {
        path: 'conversations/:id',
        loadComponent: () =>
          import(
            './features/messaging/pages/conversation-detail/conversation-detail.component'
          ).then((m) => m.ConversationDetailComponent),
      },
      {
        path: 'new',
        loadComponent: () =>
          import(
            './features/messaging/pages/conversation-detail/conversation-detail.component'
          ).then((m) => m.ConversationDetailComponent),
      },
    ],
  },
];

// Help & Support. Reached from the main menu; the whole area needs an account,
// because a ticket is a conversation attached to a person — there is nowhere to
// send the reply otherwise. AccessControlGuard covers session, RBAC and the
// per-screen access keys in one pass, the same as the messaging area.
const helpRoutes: Routes = [
  {
    path: 'help',
    // Master switch first: while support is off these routes must not resolve
    // at all, whatever the caller's permissions say.
    canActivate: [SupportEnabledGuard],
    canActivateChild: [AccessControlGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/support/pages/help/help.page').then(
            (m) => m.HelpPage,
          ),
      },
      {
        path: 'tickets',
        loadComponent: () =>
          import(
            './features/support/pages/support-ticket-list/support-ticket-list.component'
          ).then((m) => m.SupportTicketListComponent),
      },
      {
        path: 'tickets/:id',
        loadComponent: () =>
          import(
            './features/support/pages/support-ticket-thread/support-ticket-thread.component'
          ).then((m) => m.SupportTicketThreadComponent),
      },
    ],
  },
];

// Sequal browser-app surface mounted at root. Anonymous-first (no auth guard);
// the tabs shell + child pages live under app/src/app/features/sequal/.
const sequalRoutes: Routes = [
  {
    path: '',
    canActivate: [CountryAvailabilityGuard],
    loadChildren: () =>
      import('./features/sequal/tabs/sequal.routes').then((m) => m.SEQUAL_ROUTES),
  },
];

export const routes: Routes = [
  ...authRoutes,
  ...accountRoutes,
  ...messageRoutes,
  ...helpRoutes,
  ...sequalRoutes,
  {
    path: '**',
    redirectTo: CommonRoute.NOT_FOUND,
    pathMatch: 'full',
  },
];
